import { DragHandle, dragChrome } from "@/components/ui/DragHandle";
import { FILE_META } from "@/components/ui/marks";
import { MicroLabel } from "@/components/ui/MicroLabel";
import type {
  FigureLayoutResponse,
  FigureResponse,
  SetAsideFigureResponse,
} from "@patent-studio/api-types";
import { ROOT, laneOf } from "@/hooks/use-figure-layout";
import type { FigureGroup } from "@/lib/figure-labels";
import type { FigureDefineStatus } from "@/lib/figure-status";
import { cn } from "@/lib/utils";
import {
  ArrangementDrag,
  topLevelEntries,
  useFigureSortable,
  useGroupDrag,
  useTopLevelDrop,
} from "@/components/shared/arrangement-drag";
import type { ArrangementView } from "@/components/shared/arrangement-drag";
import { groupHeading, namedGroup } from "@/lib/figure-group-naming";
import { named } from "@/lib/figure-naming";
import { statusTag } from "./define-status";
import { StatusPill } from "@/components/ui/StatusPill";
import { FOCUS_RING } from "./panel-styles";
import { ScrollRows } from "@/components/ui/ScrollRows";

type Statuses = Readonly<Record<string, FigureDefineStatus>>;

/**
 * One figure, selectable, with the grip that moves it beside its name.
 *
 * Two controls rather than one: a press on the grip must not open the figure,
 * and a press on the row must not start a drag. The lane names which list the
 * row sits in, which is what lets a drag carry it into a group and out again.
 */
function NavigatorRow({
  figure,
  status,
  index,
  lane,
  selected,
  landing,
  onSelect,
}: {
  figure: FigureResponse;
  status: FigureDefineStatus;
  index: number;
  lane: string;
  selected: boolean;
  landing: boolean;
  onSelect: (fileId: string) => void;
}) {
  const { ref, handleRef, isDragging } = useFigureSortable(
    figure.file_id,
    index,
    lane,
  );
  const tag = statusTag(status);
  return (
    <li
      ref={ref}
      // The hover is the row's, grip included: a hover on the button alone left a
      // white column beside the name where the pointer said nothing was happening.
      className={cn(
        "flex items-stretch gap-0.5 rounded-sm border",
        selected
          ? "border-ring bg-card shadow-sm"
          : "border-transparent hover:bg-muted",
        dragChrome({ dragging: isDragging, landing }),
      )}
    >
      <DragHandle
        size="sm"
        handleRef={handleRef}
        label={`Reorder ${named(figure)}`}
        className="self-center"
      />
      <button
        type="button"
        data-testid={`define-nav-${figure.file_id}`}
        aria-current={selected}
        onClick={() => {
          onSelect(figure.file_id);
        }}
        // No outline of its own: the row's border marks the open figure, and a
        // browser ring inside it read as a second frame in another colour.
        className={cn(
          "flex min-w-0 flex-1 flex-col gap-0.5 rounded-sm py-[9px] pr-[11px] text-left text-detail",
          FOCUS_RING,
        )}
      >
        <span className="flex items-center gap-2">
          <span className="font-semibold">{`FIG. ${figure.label ?? ""}`}</span>
          <StatusPill tone={tag.tone} size="sm" className="ml-auto">
            {tag.label}
          </StatusPill>
        </span>
        <span className="truncate font-mono text-[10.5px] text-muted-foreground">
          {figure.original_filename}
        </span>
      </button>
      {isDragging && <span className="sr-only">Moving</span>}
    </li>
  );
}

/**
 * A deleted figure in the navigator's fold: the same row, with no grip, since a deleted figure
 * has no place in the arrangement to move to, and `Deleted` where its label was.
 */
export function DeletedNavRow({
  figure,
  selected,
  onSelect,
}: {
  figure: SetAsideFigureResponse;
  selected: boolean;
  onSelect: (fileId: string) => void;
}) {
  return (
    <div
      className={cn(
        "flex items-stretch gap-0.5 rounded-sm border",
        selected
          ? "border-ring bg-card shadow-sm"
          : "border-transparent hover:bg-muted",
      )}
    >
      <button
        type="button"
        data-testid={`define-nav-deleted-${figure.file_id}`}
        aria-current={selected}
        onClick={() => {
          onSelect(figure.file_id);
        }}
        className={cn(
          "flex min-w-0 flex-1 flex-col gap-0.5 rounded-sm px-[11px] py-[9px] text-left text-detail",
          FOCUS_RING,
        )}
      >
        <span className={FILE_META}>Deleted</span>
        <span className="truncate font-mono text-[10.5px] text-muted-foreground">
          {figure.original_filename}
        </span>
      </button>
    </div>
  );
}

/**
 * One group: a heading that moves the whole group, and the lane its members
 * sit in. The heading carries the group's own drop target, so a drop on the
 * lane goes inside and a drop on the heading goes beside, as on the Organise
 * screen.
 */
function NavigatorGroup({
  group,
  index,
  members,
  view,
  statuses,
  selectedId,
  onSelect,
}: {
  group: FigureGroup;
  index: number;
  members: string[];
  view: ArrangementView;
  statuses: Statuses;
  selectedId: string;
  onSelect: (fileId: string) => void;
}) {
  const { ref, targetRef, handleRef, isDragging, laneRef } = useGroupDrag(
    group.group_id,
    index,
  );
  const { over, byId } = view;
  const takesTheDrop =
    over !== null &&
    (over === laneOf(group.group_id) || members.includes(over));
  const heading = groupHeading(group);
  return (
    <li
      ref={ref}
      role="group"
      aria-label={heading}
      className={cn(
        "my-0.5 rounded-sm",
        dragChrome({ dragging: isDragging, landing: takesTheDrop }),
      )}
    >
      <div ref={targetRef} className="flex items-center gap-0.5 pb-1 pt-1.5">
        <DragHandle
          size="sm"
          handleRef={handleRef}
          label={`Reorder ${namedGroup(group)}`}
          className="self-center"
        />
        <MicroLabel size="sm" className="text-primary">
          {heading}
        </MicroLabel>
      </div>
      <ul
        ref={laneRef}
        className="ml-3 flex min-h-[18px] flex-col gap-1 border-l border-border pl-1"
      >
        {members.map((fileId, position) => {
          const figure = byId.get(fileId);
          return figure === undefined ? null : (
            <NavigatorRow
              key={fileId}
              figure={figure}
              status={statuses[fileId]}
              index={position}
              lane={laneOf(group.group_id)}
              selected={fileId === selectedId}
              landing={over === fileId}
              onSelect={onSelect}
            />
          );
        })}
      </ul>
    </li>
  );
}

/** The top level, as somewhere a figure can be dropped rather than only a list. */
function TopLevel({
  view,
  statuses,
  selectedId,
  onSelect,
}: {
  view: ArrangementView;
  statuses: Statuses;
  selectedId: string;
  onSelect: (fileId: string) => void;
}) {
  const root = useTopLevelDrop();
  const { over } = view;
  return (
    <ul ref={root.ref} className="flex flex-col gap-1 pb-2">
      {topLevelEntries(view).map((entry) =>
        entry.kind === "group" ? (
          <NavigatorGroup
            key={entry.id}
            group={entry.group}
            index={entry.at}
            members={entry.members}
            view={view}
            statuses={statuses}
            selectedId={selectedId}
            onSelect={onSelect}
          />
        ) : (
          <NavigatorRow
            key={entry.id}
            figure={entry.figure}
            status={statuses[entry.id]}
            index={entry.at}
            lane={ROOT}
            selected={entry.id === selectedId}
            landing={over === entry.id}
            onSelect={onSelect}
          />
        ),
      )}
    </ul>
  );
}

/**
 * The figures, in the order the arrangement puts them, one selectable, each
 * movable.
 *
 * The screen defines one drawing at a time, so this is how the attorney reaches
 * the others. Each row carries the projected label, the status the server
 * answered with, and the filename, which is the only thing on the row that
 * tells two similar drawings apart. Grouped figures sit under their group's
 * heading, as they do on the Organise screen, and the same drag rearranges
 * them here: a row to a new place, into a group or out of one, and a group as
 * a unit. It writes the layout through the same seam the Organise screen uses,
 * so a reorder means the same thing on both.
 */
export function FigureNavigator({
  taskId,
  figures,
  layout,
  statuses,
  selectedId,
  onSelect,
}: {
  taskId: string | undefined;
  figures: readonly FigureResponse[];
  layout: FigureLayoutResponse;
  /** Each figure's status off the in-flight definitions, the same projection the readout sums. */
  statuses: Statuses;
  selectedId: string;
  onSelect: (fileId: string) => void;
}) {
  return (
    <nav
      aria-label="Figures"
      data-testid="define-navigator"
      className="flex min-h-0 flex-col"
    >
      {/* The rows scroll in the room the bounded card leaves them, so a long set never
          stretches the page. The right padding keeps the scrollbar off the tags. */}
      <ScrollRows
        label="Figure list"
        bound="card"
        data-testid="define-figure-rows"
        className="flex min-h-0 flex-col gap-1 pr-1"
      >
        <ArrangementDrag taskId={taskId} figures={figures} layout={layout}>
          {(view) => (
            <TopLevel
              view={view}
              statuses={statuses}
              selectedId={selectedId}
              onSelect={onSelect}
            />
          )}
        </ArrangementDrag>
      </ScrollRows>
    </nav>
  );
}
